import { EntityId, ImageFilterName } from "@jackfruit/common"
import { SagaIterator } from "redux-saga"
import { call, put, select } from "redux-saga/effects"
import { ImageEntity } from "~/interfaces/entities/Image"
import { ImageRegionEntity } from "~/interfaces/entities/ImageRegion"
import { ImageTransformationEntity } from "~/interfaces/entities/ImageTransformation"
import { ProductPageEntity } from "~/interfaces/entities/ProductPage"
import { UploadEntity } from "~/interfaces/entities/Upload"
import { getDefaultImageTransformation } from "~/services/Utils"
import { imageRegionsSelectors } from "../state/imageRegions"
import { imagesSelectors } from "../state/images"
import {
  imageTransformations,
  imageTransformationsSelectors,
} from "../state/imageTransformations"
import { productPagesSelectors } from "../state/productPages"
import { uploadsSelectors } from "../state/uploads"
import { RootState } from "../store"
import { getLineItemPages } from "./lineItems"

export function* getImageRegion(
  imageRegionId: EntityId
): SagaIterator<ImageRegionEntity> {
  const imageRegion: ImageRegionEntity = yield select((state: RootState) =>
    imageRegionsSelectors.selectById(state, imageRegionId)
  )

  return imageRegion
}

export function* getImageRegions(
  imageRegionIds: EntityId[]
): SagaIterator<ImageRegionEntity[]> {
  const imageRegions: ImageRegionEntity[] = yield select((state: RootState) =>
    imageRegionsSelectors.selectByIds(state, imageRegionIds)
  )

  return imageRegions
}

export function* getAllImageRegionsForLineItem(
  lineItemId: EntityId
): SagaIterator<ImageRegionEntity[]> {
  const productPages: ProductPageEntity[] = yield call(
    getLineItemPages,
    lineItemId
  )
  const allImageRegionIds: EntityId[] = productPages
    .map(page => page.imageRegionIds)
    .flat()

  const allImageRegions: ImageRegionEntity[] = yield call(
    getImageRegions,
    allImageRegionIds
  )

  return allImageRegions
}

export function* getAllImageRegionsForProductPageIds(
  productPageIds: EntityId[]
): SagaIterator<ImageRegionEntity[]> {
  const productPages: ProductPageEntity[] = yield select((state: RootState) =>
    productPagesSelectors.selectByIds(state, productPageIds)
  )
  const allImageRegionIds: EntityId[] = productPages
    .map(page => page.imageRegionIds)
    .flat()

  const allImageRegions: ImageRegionEntity[] = yield call(
    getImageRegions,
    allImageRegionIds
  )

  return allImageRegions
}

export function* resetImageRegionTransformation({
  imageRegionId,
  defaultImageFilter,
}: {
  imageRegionId: EntityId
  defaultImageFilter?: ImageFilterName
}): SagaIterator<void> {
  const imageRegion: ImageRegionEntity = yield call(
    getImageRegion,
    imageRegionId
  )

  const { uploadId } = imageRegion

  if (uploadId) {
    const upload: UploadEntity = yield select((state: RootState) =>
      uploadsSelectors.selectById(state, uploadId)
    )
    const image: ImageEntity = yield select((state: RootState) =>
      imagesSelectors.selectById(state, upload.imageId)
    )

    const imageTransformation: ImageTransformationEntity = yield select(
      (state: RootState) =>
        imageTransformationsSelectors.selectById(state, image.imageTransformId)
    )

    const imageDimensions = {
      width: image.width,
      height: image.height,
    }

    const targetWindow = {
      width: imageRegion.window.width,
      height: imageRegion.window.height,
    }

    const defaultImageTransformation = getDefaultImageTransformation(
      targetWindow,
      imageDimensions
    )

    yield put(
      imageTransformations.actions.updateOne({
        id: imageTransformation.id,
        changes: {
          rotation: 0,
          translation: {
            x: defaultImageTransformation.x,
            y: defaultImageTransformation.y,
          },
          zoom: defaultImageTransformation.zoom,
          minZoom: defaultImageTransformation.zoom,
          dirty: false,
          imageFilterName: defaultImageFilter,
        },
      })
    )
  }
}
